import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3, TrendingUp, TrendingDown, AlertTriangle, Users, Activity,
  Search, Calendar, Eye, ArrowUpRight, ArrowDownRight, Minus, RefreshCw,
  Beaker, UserCheck, Clock, Heart, Zap, Shield, Target, Phone,
  ChevronLeft, ChevronRight, ExternalLink,
} from 'lucide-react';
import { resultsAnalysisAPI, branchesAPI } from '../../services/api';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from 'recharts';
import toast from 'react-hot-toast';

const COLORS = ['#3B82F6', '#EF4444', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899'];
const FLAG = {
  H: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'مرتفع', icon: ArrowUpRight },
  L: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'منخفض', icon: ArrowDownRight },
};

const today = new Date().toISOString().split('T')[0];
const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const formatDate = (d) => d ? new Date(d).toLocaleDateString('ar-SA-u-ca-gregory', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '-';
const calcAge = (dob) => dob ? Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : '-';

/* ── Stat card ── */
const StatCard = ({ icon: Icon, label, value, sub, color = 'blue' }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 relative overflow-hidden hover:shadow-md transition-shadow`}>
    <div className={`absolute top-0 right-0 w-1 h-full bg-${color}-500 rounded-l`} />
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[11px] text-slate-500 font-medium mb-1">{label}</p>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
      <div className={`w-9 h-9 rounded-xl bg-${color}-50 flex items-center justify-center shrink-0`}>
        <Icon className={`w-4.5 h-4.5 text-${color}-600`} />
      </div>
    </div>
  </div>
);

/* ═══════════ MAIN ═══════════ */
const ResultsAnalysis = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('critical');
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);

  const [filters, setFilters] = useState({ branch_id: '', date_from: ninetyDaysAgo, date_to: today, flag: '', search: '' });

  const [overview, setOverview] = useState(null);
  const [criticalPatients, setCriticalPatients] = useState({ items: [], total: 0 });
  const [followUp, setFollowUp] = useState({ items: [], total: 0 });
  const [topTests, setTopTests] = useState([]);
  const [trend, setTrend] = useState([]);
  const [demographics, setDemographics] = useState({ by_gender: [], by_age_group: [] });
  const [criticalPage, setCriticalPage] = useState(1);
  const [followUpPage, setFollowUpPage] = useState(1);
  const [timeFilter, setTimeFilter] = useState('1M');

  const loadBranches = useCallback(async () => { try { const r = await branchesAPI.getAll({ limit: 100 }); setBranches(r.data?.data?.items || r.data?.data || []); } catch {} }, []);

  const loadOverview = useCallback(async () => { try { const r = await resultsAnalysisAPI.getOverview(filters); setOverview(r.data?.data); } catch {} }, [filters]);
  const loadCritical = useCallback(async (p = 1) => { try { const r = await resultsAnalysisAPI.getCriticalPatients({ ...filters, page: p, limit: 15 }); setCriticalPatients({ items: r.data?.data?.items || [], total: r.data?.data?.total || 0 }); setCriticalPage(p); } catch {} }, [filters]);
  const loadFollowUp = useCallback(async (p = 1) => { try { const r = await resultsAnalysisAPI.getFollowUp({ ...filters, time_filter: timeFilter, page: p, limit: 15 }); setFollowUp({ items: r.data?.data?.items || [], total: r.data?.data?.total || 0 }); setFollowUpPage(p); } catch {} }, [filters, timeFilter]);
  const loadTopTests = useCallback(async () => { try { const r = await resultsAnalysisAPI.getTopAbnormalTests(filters); setTopTests(r.data?.data || []); } catch {} }, [filters]);
  const loadTrend = useCallback(async () => { try { const r = await resultsAnalysisAPI.getTrend(filters); setTrend(r.data?.data || []); } catch {} }, [filters]);
  const loadDemographics = useCallback(async () => { try { const r = await resultsAnalysisAPI.getDemographics(filters); setDemographics(r.data?.data || { by_gender: [], by_age_group: [] }); } catch {} }, [filters]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadOverview(), loadCritical(1), loadTopTests(), loadTrend(), loadDemographics()]);
    setLoading(false);
  }, [loadOverview, loadCritical, loadTopTests, loadTrend, loadDemographics]);

  useEffect(() => { loadBranches(); }, [loadBranches]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (activeTab === 'followup') loadFollowUp(1); }, [activeTab, loadFollowUp]);

  const TABS = [
    { id: 'critical', label: 'مرضى بحاجة اهتمام', icon: AlertTriangle },
    { id: 'followup', label: 'متابعة المرضى', icon: UserCheck },
    { id: 'analytics', label: 'تحليلات وإحصائيات', icon: BarChart3 },
  ];

  const critPages = Math.ceil(criticalPatients.total / 15);
  const fuPages = Math.ceil(followUp.total / 15);
  const openPatientProfile = (id) => {
    if (!id || id === 'undefined' || id === 'null') {
      toast.error('لا يمكن فتح ملف المريض: المعرف غير متوفر');
      return;
    }
    navigate(`/lab/analysis/patient/${id}`);
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6" dir="rtl">
      {/* Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">تحليل النتائج المخبرية</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">مراقبة المرضى ذوي النتائج الحرجة وتتبع المتابعات</p>
        </div>
        <button onClick={loadAll} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition-all disabled:opacity-50 shrink-0">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> تحديث
        </button>
      </div>

      {/* Stats */}
      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <StatCard icon={Users} label="إجمالي المرضى" value={overview.total_patients} color="blue" />
          <StatCard icon={AlertTriangle} label="مرضى بنتائج غير طبيعية" value={overview.patients_with_abnormal} color="red" sub={`${overview.total ? ((overview.abnormal / overview.total) * 100).toFixed(1) : 0}% من النتائج`} />
          <StatCard icon={TrendingUp} label="نتائج مرتفعة" value={overview.high} color="orange" />
          <StatCard icon={TrendingDown} label="نتائج منخفضة" value={overview.low} color="cyan" />
          <StatCard icon={Activity} label="إجمالي النتائج" value={overview.total} color="emerald" />
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 bg-white rounded-2xl border border-slate-200 p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <select value={filters.branch_id} onChange={(e) => setFilters((p) => ({ ...p, branch_id: e.target.value }))} className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
            <option value="">كل الفروع</option>
            {branches.map((b) => <option key={b._id} value={b._id}>{b.branch_name}</option>)}
          </select>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))} className="px-3 py-2 rounded-xl border border-slate-200 text-sm" />
          <span className="text-slate-400">→</span>
          <input type="date" value={filters.date_to} onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))} className="px-3 py-2 rounded-xl border border-slate-200 text-sm" />
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
            <input value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} placeholder="بحث بالاسم أو الهوية أو الجوال" className="w-full pr-10 px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-blue-400" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-1 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${activeTab === t.id ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <AnimatePresence mode="wait">
          {/* ═══ CRITICAL PATIENTS TAB ═══ */}
          {activeTab === 'critical' && (
            <motion.div key="critical" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex items-center gap-2 mb-3">
                {[{ v: '', l: 'الكل' }, { v: 'H', l: 'مرتفع' }, { v: 'L', l: 'منخفض' }].map((f) => (
                  <button key={f.v} onClick={() => setFilters((p) => ({ ...p, flag: f.v }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${filters.flag === f.v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                    {f.l}
                  </button>
                ))}
                <span className="text-xs text-slate-400 mr-auto">{criticalPatients.total} مريض</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {criticalPatients.items.length === 0 && (
                  <div className="col-span-full py-12 text-center text-sm text-slate-400">
                    <Heart className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                    لا يوجد مرضى بنتائج غير طبيعية في الفترة المحددة
                  </div>
                )}
                {criticalPatients.items.map((p, i) => (
                  <motion.div key={p._id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                    className="bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-lg transition-all group">
                    {/* Patient header */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shrink-0">
                        <span className="text-base font-bold text-blue-700">{p.patient_name?.charAt(0) || '?'}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-900 truncate">{p.patient_name}</p>
                        <p className="text-[10px] text-slate-400">ID: {p.patient_id || '-'} • {p.patient_gender === 'Male' ? 'ذكر' : 'أنثى'} • {calcAge(p.patient_dob)} سنة</p>
                      </div>
                      <div className="text-center shrink-0">
                        <p className="text-lg font-bold text-red-600">{p.total_abnormal}</p>
                        <p className="text-[9px] text-slate-400">غير طبيعي</p>
                      </div>
                    </div>

                    {/* Abnormal badges */}
                    <div className="flex items-center gap-2 mb-3">
                      {p.high_count > 0 && <span className="text-[10px] px-2 py-0.5 rounded-md bg-red-50 text-red-600 font-bold border border-red-100">{p.high_count} مرتفع</span>}
                      {p.low_count > 0 && <span className="text-[10px] px-2 py-0.5 rounded-md bg-blue-50 text-blue-600 font-bold border border-blue-100">{p.low_count} منخفض</span>}
                      <span className="text-[10px] text-slate-400 mr-auto">{p.visit_count} زيارة</span>
                    </div>

                    {/* Latest abnormal details */}
                    <div className="space-y-1.5 mb-3">
                      {(p.abnormal_details || []).slice(0, 3).map((d, di) => {
                        const f = FLAG[d.flag];
                        return (
                          <div key={di} className="flex items-center gap-2 text-[11px] px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-100">
                            {f && <f.icon className={`w-3 h-3 ${f.text} shrink-0`} />}
                            <span className="text-slate-700 truncate flex-1">{d.test_name} — {d.component_name || '-'}</span>
                            <span className="font-bold text-slate-800 shrink-0">{d.value ?? '-'} {d.unit || ''}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                      <button onClick={() => openPatientProfile(p._id)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-100 transition-colors">
                        <Eye className="w-3.5 h-3.5" /> ملف المريض الكامل
                      </button>
                      {p.patient_phone && (
                        <a href={`tel:${p.patient_phone}`} className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-100 transition-colors">
                          <Phone className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {critPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button disabled={criticalPage <= 1} onClick={() => loadCritical(criticalPage - 1)} className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
                  <span className="text-xs text-slate-500">{criticalPage} / {critPages}</span>
                  <button disabled={criticalPage >= critPages} onClick={() => loadCritical(criticalPage + 1)} className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
                </div>
              )}
            </motion.div>
          )}

          {/* ═══ FOLLOW-UP TAB ═══ */}
          {activeTab === 'followup' && (
            <motion.div key="followup" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm text-slate-600">نتائج أقدم من:</span>
                {['1M', '2M', '3M'].map((tf) => (
                  <button key={tf} onClick={() => setTimeFilter(tf)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${timeFilter === tf ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                    {tf === '1M' ? 'شهر' : tf === '2M' ? 'شهرين' : '3 أشهر'}
                  </button>
                ))}
                <span className="text-xs text-slate-400 mr-auto">{followUp.total} مريض</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {followUp.items.length === 0 && (
                  <div className="col-span-full py-12 text-center text-sm text-slate-400">لا يوجد مرضى يحتاجون متابعة</div>
                )}
                {followUp.items.map((item, i) => (
                  <motion.div key={`${item._id?.customer_id}-${item._id?.test_id}-${i}`}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                    className="bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center shrink-0">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-slate-900 truncate">{item.patient_name || '-'}</p>
                        <p className="text-[10px] text-slate-400">ID: {item.patient_id || '-'}</p>
                      </div>
                    </div>
                    <div className="space-y-1.5 text-xs mb-3">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Beaker className="w-3.5 h-3.5 text-slate-400" />
                        <span>آخر تحليل غير طبيعي: <strong>{item.test_name || '-'}</strong></span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        <span>{formatDate(item.last_abnormal_date)}</span>
                        <span className="text-slate-400">({Math.round((Date.now() - new Date(item.last_abnormal_date).getTime()) / (86400000))} يوم)</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {item.high_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-bold border border-red-100">{item.high_count} HIGH</span>}
                        {item.low_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-bold border border-blue-100">{item.low_count} LOW</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                      <button onClick={() => openPatientProfile(item.customer_id)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-100">
                        <Eye className="w-3.5 h-3.5" /> ملف المريض
                      </button>
                      {item.patient_phone && (
                        <a href={`tel:${item.patient_phone}`} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-100">
                          <Phone className="w-3.5 h-3.5" /> تواصل
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              {fuPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button disabled={followUpPage <= 1} onClick={() => loadFollowUp(followUpPage - 1)} className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
                  <span className="text-xs text-slate-500">{followUpPage} / {fuPages}</span>
                  <button disabled={followUpPage >= fuPages} onClick={() => loadFollowUp(followUpPage + 1)} className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
                </div>
              )}
            </motion.div>
          )}

          {/* ═══ ANALYTICS TAB ═══ */}
          {activeTab === 'analytics' && (
            <motion.div key="analytics" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
                {/* Trend */}
                <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
                  <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" /> اتجاه النتائج غير الطبيعية
                  </h3>
                  {trend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <AreaChart data={trend}>
                        <defs>
                          <linearGradient id="gH" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EF4444" stopOpacity={0.15} /><stop offset="100%" stopColor="#EF4444" stopOpacity={0} /></linearGradient>
                          <linearGradient id="gL" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.15} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
                          <linearGradient id="gN" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.1} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="_id" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                        <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                        <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                        <Area type="monotone" dataKey="normal" name="طبيعي" stroke="#10B981" fill="url(#gN)" strokeWidth={2} />
                        <Area type="monotone" dataKey="high" name="مرتفع" stroke="#EF4444" fill="url(#gH)" strokeWidth={2} />
                        <Area type="monotone" dataKey="low" name="منخفض" stroke="#3B82F6" fill="url(#gL)" strokeWidth={2} />
                        <Legend verticalAlign="top" height={36} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">لا توجد بيانات</div>}
                </div>

                {/* Top tests */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Target className="w-4 h-4 text-red-500" /> أكثر التحاليل بنتائج غير طبيعية
                  </h3>
                  <div className="space-y-2.5 max-h-[260px] overflow-y-auto">
                    {topTests.length === 0 && <p className="text-sm text-slate-400 text-center py-6">لا توجد بيانات</p>}
                    {topTests.map((t, i) => {
                      const max = Math.max(...topTests.map((x) => x.count || 0), 1);
                      const width = Math.max(10, Math.round(((t.count || 0) / max) * 100));
                      return (
                        <div key={t._id || i} className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5">
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-[10px] font-bold text-slate-400 w-4 shrink-0">{i + 1}</span>
                              <span className="text-xs sm:text-sm font-semibold text-slate-800 truncate">{t.name || 'غير مسمى'}</span>
                            </div>
                            <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md text-slate-600 shrink-0">
                              {(t.affected || 0)} مريض
                            </span>
                          </div>
                          <div className="w-full h-1.5 rounded-full bg-slate-200 overflow-hidden mb-1.5">
                            <div className="h-full rounded-full bg-red-400" style={{ width: `${width}%` }} />
                          </div>
                          <div className="flex items-center justify-between text-[10px]">
                            <span className="text-slate-500">إجمالي غير طبيعي: <strong className="text-slate-700">{t.count || 0}</strong></span>
                            <div className="flex items-center gap-2">
                              <span className="text-red-600 font-bold">مرتفع {t.high || 0}</span>
                              <span className="text-blue-600 font-bold">منخفض {t.low || 0}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Demographics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4 text-violet-500" /> توزيع حسب الجنس
                  </h3>
                  {demographics.by_gender.length > 0 ? (
                    <div className="flex items-center justify-center gap-8">
                      <ResponsiveContainer width={130} height={130}>
                        <PieChart>
                          <Pie data={demographics.by_gender.map((g) => ({ name: g._id === 'Male' ? 'ذكر' : 'أنثى', value: g.count }))} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" stroke="none">
                            {demographics.by_gender.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-2">
                        {demographics.by_gender.map((g, i) => (
                          <div key={g._id} className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                            <span className="text-sm text-slate-700">{g._id === 'Male' ? 'ذكر' : 'أنثى'}</span>
                            <span className="text-sm font-bold text-slate-900">{g.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : <div className="h-[130px] flex items-center justify-center text-slate-400 text-sm">لا توجد بيانات</div>}
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-pink-500" /> توزيع حسب الفئة العمرية
                  </h3>
                  {demographics.by_age_group.length > 0 ? (
                    <ResponsiveContainer width="100%" height={130}>
                      <BarChart data={demographics.by_age_group}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                        <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                        <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                        <Bar dataKey="count" name="عدد" radius={[6, 6, 0, 0]}>
                          {demographics.by_age_group.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="h-[130px] flex items-center justify-center text-slate-400 text-sm">لا توجد بيانات</div>}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default ResultsAnalysis;
